import { useEffect, useMemo, useRef } from 'react';
import { useReducedMotion } from 'motion/react';
import type { StoryHighlight, StoryModel } from './story-model';
import { applyStoryTransforms } from './story-dom';
import { StoryCard } from './story-card';
import { useStoryRenderWindow } from './use-story-render-window';
import type { TimelinePositionEngine } from './use-timeline-position';

interface StoryStageProps {
  readonly model: StoryModel;
  readonly engine: TimelinePositionEngine;
  readonly selectedId: string | null;
  readonly onHoverItem: (item: StoryHighlight) => void;
  readonly onClearHover: () => void;
  readonly onToggleDetail: (item: StoryHighlight) => void;
}

export function StoryStage({
  model,
  engine,
  selectedId,
  onHoverItem,
  onClearHover,
  onToggleDetail,
}: StoryStageProps) {
  const cardsRef = useRef<HTMLDivElement>(null);
  const stageSurfaceRef = useRef<HTMLDivElement>(null);
  const dateLabelRef = useRef<HTMLSpanElement>(null);
  const periodLabelRef = useRef<HTMLSpanElement>(null);
  const liveDateRef = useRef<HTMLParagraphElement>(null);
  const reducedMotion = useReducedMotion() ?? false;

  const range = useStoryRenderWindow({
    model,
    engine,
    cardsRef,
    dateLabelRef,
    activePeriodLabelRef: periodLabelRef,
    stageSurfaceRef,
    liveDateRef,
    reducedMotion,
  });

  const visibleItems = useMemo(
    () => model.items.slice(range.start, range.end),
    [model.items, range.end, range.start],
  );

  useEffect(() => {
    applyStoryTransforms(cardsRef.current, engine.get(), reducedMotion);
  }, [engine, reducedMotion, selectedId]);

  return (
    <main className="relative flex-1 overflow-hidden">
      <div ref={stageSurfaceRef} className="absolute inset-0 bg-surface-raised" />
      {/* pr-64 holder datoteksten klar av handlingsknappene i StoryToolbar. */}
      <header className="pointer-events-none absolute left-0 right-0 top-0 z-20 border-b border-white/10 bg-surface/75 py-4 pl-6 pr-64 backdrop-blur">
        <p className="text-xs uppercase tracking-[0.18em] text-text-tertiary">Aktiv dato</p>
        <span ref={dateLabelRef} className="block text-2xl font-semibold text-text-primary">
          -
        </span>
        <span
          ref={periodLabelRef}
          className="mt-2 hidden max-w-fit rounded-full border border-white/12 bg-white/6 px-3 py-1 text-sm font-medium text-text-primary shadow-sm"
        />
      </header>

      <section
        aria-hidden
        className="absolute inset-0 mt-24 overflow-hidden px-3"
        onPointerLeave={onClearHover}
      >
        <div ref={cardsRef} className="relative h-full w-full">
          {visibleItems.map((item, index) => (
            <StoryCard
              key={item.id}
              item={item}
              index={range.start + index}
              isExpanded={selectedId === item.id}
              onHoverStart={onHoverItem}
              onToggleExpand={onToggleDetail}
            />
          ))}
        </div>
      </section>

      <p className="sr-only" aria-live="polite" ref={liveDateRef}>
        -
      </p>
    </main>
  );
}
